/**
 * `<PeriodSelect>` — FR-54's picker (plan D55): the presets of `periods.ts`
 * in IST, an optional *any time* (the library's default), and a from–to pair
 * for *Custom* that **refuses a start after its end**: the range is not asked
 * until it is one, and the picker says why.
 *
 * The library's *Document date* and *Uploaded* filters are two of these; the
 * home screen's period is a third, without *any time*. Kit primitives only.
 */
import { Input, Select, TONE_TEXT, cn } from '@fiveox/ui'
import { useEffect, useId, useState } from 'react'

import { PERIOD_PRESETS, PERIOD_PRESET_LABELS, customPeriodError, istToday, parsePeriodPreset, type PeriodValue } from './periods'

export interface PeriodSelectProps {
  /** The control's name, said to a screen reader — and, with `prefixed`, before each choice. */
  label: string
  value: PeriodValue
  /** Called with a value that may be asked: a preset, *any time*, or a custom range that is one. */
  onChange: (next: PeriodValue) => void
  /** Offer *any time* under these words — the library's default. Omitted, a preset is always chosen. */
  anyLabel?: string
  /** "Uploaded: this month" — each choice named with the label, as the library's bar shows them. */
  prefixed?: boolean
  /** The IST day *Custom* starts on — today unless a test says otherwise. */
  today?: string
  className?: string
}

export function PeriodSelect({ label, value, onChange, anyLabel, prefixed = false, today, className }: PeriodSelectProps) {
  const id = useId()
  // The kit's old names arrive from a saved address as today's (`this_fy` is `this_year`); `last7` keeps its own choice.
  const preset = value.preset === 'any' ? 'any' : (parsePeriodPreset(value.preset) ?? 'custom')
  const [from, setFrom] = useState(value.from)
  const [to, setTo] = useState(value.to)
  useEffect(() => {
    setFrom(value.from)
    setTo(value.to)
  }, [value.from, value.to])
  const error = preset === 'custom' ? customPeriodError(from, to) : null
  const named = (text: string) => (prefixed ? `${label}: ${text.toLowerCase()}` : text)

  const typed = (nextFrom: string, nextTo: string) => {
    setFrom(nextFrom)
    setTo(nextTo)
    // Refused while the start is after the end: the range on screen is not asked.
    if (!customPeriodError(nextFrom, nextTo)) onChange({ preset: 'custom', from: nextFrom, to: nextTo })
  }

  return (
    <span className={cn('flex flex-wrap items-center gap-2', className)}>
      <Select
        size="sm"
        aria-label={label}
        value={preset}
        onChange={(e) => {
          const next = e.target.value as PeriodValue['preset']
          // Only a choice this picker offers is a question.
          if (next !== 'any' && !parsePeriodPreset(next)) return
          if (next === 'custom') {
            const day = today ?? istToday()
            onChange({ preset: 'custom', from: value.from || day, to: value.to || day })
          } else onChange({ preset: next, from: '', to: '' })
        }}
      >
        {anyLabel !== undefined && <option value="any">{prefixed ? `${label}: ${anyLabel.toLowerCase()}` : anyLabel}</option>}
        {PERIOD_PRESETS.map((p) => (
          <option key={p} value={p}>
            {named(PERIOD_PRESET_LABELS[p])}
          </option>
        ))}
        {preset === 'last7' && <option value="last7">{named(PERIOD_PRESET_LABELS.last7)}</option>}
      </Select>
      {preset === 'custom' && (
        <>
          <Input
            size="sm"
            type="date"
            id={`${id}-from`}
            aria-label={`${label} from`}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            value={from}
            max={to || undefined}
            onChange={(e) => typed(e.target.value, to)}
          />
          <span className="text-muted-foreground text-xs">to</span>
          <Input
            size="sm"
            type="date"
            aria-label={`${label} to`}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            value={to}
            min={from || undefined}
            onChange={(e) => typed(from, e.target.value)}
          />
          {error && (
            <span id={`${id}-error`} role="alert" className={cn('basis-full text-xs', TONE_TEXT.error)}>
              {error}
            </span>
          )}
        </>
      )}
    </span>
  )
}
